import CollaborationCard from "Components/campaigns/collaborations/CollaborationCard";
import { PRIVILEGE } from "constants/privilege";
import withAccessControl from "hoc/withAccessControl";
import useDocumentTitle from "hooks/useDocumentTitle";
import CampaignLayout from "Layouts/CampaignLayout"
import { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { setCollaborationsSearchQeury, updateCollaborationFilters } from "slices/campaigns/collaborations/reducer";
import { fetchCollaborations } from "slices/campaigns/collaborations/thunk";
import { AppDispatch, RootState } from "store";
import { CampaignCollaborationsFilters, SortOptionLabel, SortOptions } from "types/campaign/collaborations";
import { decodeBase64, encodeBase64, generateUniqueKey } from "utils/helpers";

export const defaultAppliedFilters: CampaignCollaborationsFilters = {
    page: 1,
    sort: SortOptions.MOST_RECENT
};

export const defaultCollaborationFilters: CampaignCollaborationsFilters = {
    page: 1,
    sort: SortOptions.MOST_RECENT,
    q: ''
};


const CampaignCollaborations = () => {

    useDocumentTitle("Collaborations - Campaign");

    const { campaignId } = useParams();
    const campaign = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign);
    const dispatch: AppDispatch = useDispatch();

    const [searchParams, setSearchParams] = useSearchParams();
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const queryFilters = searchParams.get('filters');
    useEffect(() => {
        const decodedFilters = queryFilters ? decodeBase64(queryFilters) : defaultCollaborationFilters;
        dispatch(updateCollaborationFilters({ ...decodedFilters, page: 1 }))
    }, [queryFilters])

    const [searchQuery, setSearchQuery] = useState('');

    const { filters } = useSelector((state: RootState) => state.collaborations);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));

    const collaborationsData = useSelector((state: RootState) => state.collaborations.campaigns[campaignId!]?.filtered_lists?.[filterId!]);
    const collaborations = collaborationsData?.list ?? [];
    const page = collaborationsData?.page ?? 1;
    const has_more = collaborationsData?.has_more ?? true;

    const loading = useSelector((state: RootState) => state.collaborations.loading);
    const error = useSelector((state: RootState) => state.collaborations.error);

    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        if (campaignId) {
            if (filters.page && filters.page === 1) {
                setInitialLoading(true);
            }
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            debounceTimeout.current = setTimeout(() => {
                dispatch(fetchCollaborations({ filters: filters, campaignId }))
                    .finally(() => {
                        setFilterId(generateUniqueKey(filters, ['page']));
                        setInitialLoading(false);
                    })
            }, 500)
        }
    }, [campaignId, filters])

    const nextPage = () => {
        if (has_more && campaignId && !loading) {
            const nextPageNumber = page + 1;
            dispatch(fetchCollaborations({ filters: { ...filters, page: nextPageNumber }, campaignId }))
        }
    }

    const setFilters = (updatedFilters: CampaignCollaborationsFilters) => {
        const encodedFilters = encodeBase64(updatedFilters);
        setSearchParams({ filters: encodedFilters });
        // dispatch(updateApplicationFilters(updatedFilters));
    }

    useEffect(() => {
        if (filters.q) {
            setSearchQuery(filters.q);
        }
    }, [filters.q])

    const updateSorting = (sortOption: SortOptions) => {
        let updated_filters = { ...filters, sort: sortOption };
        delete updated_filters.order;
        if ([SortOptions.LOWEST_CPE, SortOptions.LOWEST_CPV].includes(sortOption)) {
            updated_filters.order = 'asc';
        }
        setFilters(updated_filters);
    }


    // useEffect(() => {
    //     if (debounceTimeout.current) {
    //         clearTimeout(debounceTimeout.current);
    //     }

    //     debounceTimeout.current = setTimeout(() => {
    //         if (searchQuery.trim().length > 2 || searchQuery.trim().length === 0) {
    //             dispatch(setCollaborationsSearchQeury(searchQuery))
    //         }
    //     }, 500);

    //     return () => {
    //         if (debounceTimeout.current) {
    //             clearTimeout(debounceTimeout.current);
    //         }
    //     };
    // }, [searchQuery, dispatch]);

     const searchTask = (q: string) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (q.trim().length > 3) {
                setFilters({ ...filters, q: q.trim(), page: 1 });
            } else if (q.trim().length === 0) {
                setFilters({ ...filters, q: '', page: 1 });
            }
        }, 500);
        setSearchQuery(q);
    }

    return (
        <CampaignLayout
            filters={
                <div className="d-flex justify-content-end align-items-center">
                    <div className="campaign-layout-search-container position-relative me-2">
                        <span>
                            <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z" fill="#BBD8ED" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <input type="text" placeholder='Search...' value={searchQuery} onChange={(e) => searchTask(e.target.value)} className="campaign-layout-search-input" />
                        {
                            searchQuery && (
                                <span className="search-clear-icon" onClick={() => {
                                    searchTask('');
                                }}>
                                    <i className='bx bx-x' />
                                </span>
                            )}
                    </div>
                    <UncontrolledDropdown className='sort-dropdown me-2'>
                        <DropdownToggle tag={"button"} className='btn btn-white d-flex justify-content-between align-items-center w-100'>
                            <span>
                                <span className='text-muted me-1'>Sort by: </span>
                                {SortOptionLabel[filters.sort]}
                            </span>
                            <i className='mdi mdi-chevron-down' />
                        </DropdownToggle>
                        <DropdownMenu className='mt-1 p-0 w-100'>
                            {
                                Object.values(SortOptions).map((sortOption, i) => <DropdownItem key={i} onClick={() => updateSorting(sortOption)}>
                                    {SortOptionLabel[sortOption]}
                                </DropdownItem>
                                )
                            }
                        </DropdownMenu>
                    </UncontrolledDropdown>
                    {
                        campaign?.mode !== 'offline' &&
                        <Link to={{pathname: `/campaign/${campaignId}/collaborations/bulk-process`}} className='btn btn-white d-flex justify-content-between align-items-center w-100 bulk-process-btn'>
                            <i className="ri-stack-fill"></i>
                            Bulk Process
                        </Link>
                    }

                </div>
            }
        >
            <InfiniteScroll
                dataLength={collaborations.length}
                next={nextPage}
                hasMore={has_more}
                loader={<></>}
            >
                {
                    !initialLoading && collaborations.map((collaboration, i) => <CollaborationCard key={i} collaboration={collaboration} />)
                }
                {
                    (loading || initialLoading) && Array.from({ length: 4 }).map((_, i) => (
                        <div key={i}>
                            <Skeleton height={336} className='w-100 d-block' />
                        </div>
                    ))
                }
                {
                    !(loading || initialLoading) && collaborations.length === 0 &&
                    <div className="d-flex w-100 justify-content-center align-items-center">
                        <div className="length-zero">
                            <h3>Currently there is no collaboration in your list</h3>
                        </div>
                    </div>
                }
                {
                    !(loading || initialLoading) && error &&
                    <div className="d-flex justify-content-center align-items-center">
                        <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                    </div>
                }
            </InfiniteScroll>
        </CampaignLayout>
    )
}

export default withAccessControl(CampaignCollaborations, PRIVILEGE.CAMPAIGN.COLLABORATION.INDEX)